import { HarvestType } from '@/common/constants/harvest.constants';
import { ISelectOption } from '@/common/models';
import { determineClassByInterval } from '@/lib/utils';
import { useEffect, useMemo, useRef } from 'react';
import {
  FieldValues,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { toast } from 'sonner';

export interface DetermineClassProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  watchedPollution: string | null | undefined;
  watchedHumidity: string | null | undefined;
  industrialGrade: number | string | undefined;
  clearErrors: UseFormClearErrors<TFieldValues>;
  classList: ISelectOption[];
  setValue: UseFormSetValue<TFieldValues>;
  harvest_type: string | undefined;
}

export const useDetermineClass = ({
  watchedPollution,
  watchedHumidity,
  industrialGrade,
  clearErrors,
  classList,
  setValue,
  harvest_type,
}: DetermineClassProps) => {
  const lastToastClassRef = useRef<string | null>(null);

  const filterClassList = useMemo(() => {
    if (harvest_type === HarvestType.MANUAL) {
      return classList ?? [];
    }
    if (
      harvest_type === HarvestType.GROUND_HARVESTED ||
      harvest_type === HarvestType.COMBINE
    ) {
      const ig = Number(industrialGrade);
      if (ig === 1) {
        return (classList ?? []).filter((el) => String(el.value) !== '1');
      }
      return classList ?? [];
    }
    return classList ?? [];
  }, [harvest_type, industrialGrade, classList]);

  useEffect(() => {
    if (
      watchedPollution !== undefined &&
      watchedPollution !== null &&
      watchedPollution !== '' &&
      industrialGrade !== undefined &&
      industrialGrade !== null &&
      industrialGrade !== '' &&
      watchedHumidity !== undefined &&
      watchedHumidity !== null &&
      watchedHumidity !== ''
    ) {
      const pollution = Number(watchedPollution);
      const igNum = Number(industrialGrade);

      if (!Number.isNaN(pollution)) {
        clearErrors(['pollution', 'class']);

        const finalClass = determineClassByInterval(
          pollution,
          igNum,
          harvest_type,
        );

        if (finalClass) {
          const isValidForHarvestType = filterClassList?.some(
            (item) => String(item.value) === String(finalClass),
          );

          if (isValidForHarvestType) {
            setValue('class', finalClass);
            clearErrors('class');

            if (lastToastClassRef.current !== finalClass) {
              if (
                (harvest_type === HarvestType.COMBINE ||
                  harvest_type === HarvestType.GROUND_HARVESTED) &&
                igNum === 1 &&
                finalClass === '2'
              ) {
                toast.success('Avtomatik 2-sinfga o‘tkazildi!', {
                  id: 'class-auto-change-toast',
                });
              } else {
                toast.success(`${finalClass}-sinfga mos keldi!`, {
                  id: `class-match-toast-${finalClass}`,
                });
              }
              lastToastClassRef.current = finalClass;
            }
          } else {
            setValue('class', '');
            lastToastClassRef.current = null;
          }
        } else {
          setValue('class', '');
          lastToastClassRef.current = null;
        }
      }
    } else {
      setValue('class', '');
      clearErrors(['pollution', 'class']);
      lastToastClassRef.current = null;
    }
  }, [
    watchedPollution,
    watchedHumidity,
    industrialGrade,
    filterClassList,
    setValue,
    clearErrors,
    harvest_type,
  ]);

  return {
    filterClassList,
  };
};
